// Journey-Stage Adaptive Dashboards — Seller Home (note-holder dashboard).
//
// A stage-adaptive landing for the note-holding seller, composed through the
// shared portal dashboard ENGINE (../dashboard/engine): PortalHomeLayout owns
// the page chrome + the primary fetch's loading/error/retry; the journey
// spine and stage-derived "next step" hero render from the loaded payload;
// the note-holder widget area (stat tiles, payoff countdown, income trend,
// note roster) is a PortalHomeRecipe of boundary-protected widgets. The
// seller keeps ONE composed `GET seller/finance/home` primary fetch — the
// widgets render from that payload, they do not re-fetch.
//
// Money honesty (F-03, locked): the hero "next payment" and the YTD tile are
// the BUYER's GROSS payments on the note (never the seller's net carve —
// which is never re-derived here); both carry an explicit gross caption. The
// trend and roster stay NET seller-routed disbursements ("Distributed to
// you") — the labels distinguish the two bases.

import { useCallback, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Button,
  EmptyState,
  StatusBadge,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency } from "@/design-system/lib/format";
import {
  JourneySpine,
  NextBestActionCard,
  ProgressBar,
  StatTile,
  StatTileRow,
  type WidgetTone,
} from "../dashboard/DashboardPrimitives";
import {
  PortalHomeDashboard,
  PortalHomeLayout,
  useWidgetData,
  WidgetErrorBoundary,
  WidgetFrame,
  type PortalHomeRecipe,
} from "../dashboard/engine";
import { PortalRequestsStrip } from "../components/PortalRequestsStrip";
import { asSellerStage, SELLER_SPINE, sellerSpine, type SellerStage } from "../dashboard/journey";
import {
  portalSellerFinanceApi,
  type SellerHome,
  type SellerHomeHeroNote,
  type SellerIncomePoint,
} from "../api/sellerFinance";

/** F-03 (locked): the hero next-payment and YTD figures are the buyer's GROSS
 *  payments on the note — the Phoenix spread/fee carve is never re-derived on
 *  this surface. Portal twin of the staff `SellerInterestIsGross` caption
 *  convention (PR #671).
 *
 *  The caption ENUMERATES the carves, so it goes stale the moment a new one
 *  ships — which is exactly what the buyer consulting fee did to it. Anything
 *  that takes money out of the seller's distribution belongs in this list, and
 *  the per-period amounts are itemized on the accounting tab. */
const GROSS_FIGURES_CAPTION =
  "Gross buyer payments — your distribution is net of servicing, broker, and consulting fees.";

// F-42: recharts wants raw values, not Tailwind classes. These named constants
// mirror the design tokens (design-system/tokens.json — outside the portal
// import allow-list, so the values are pinned here with their token names).
const CHART_TICK_FONT_SIZE = "0.8125rem"; // tokens.json typography.fontSize.caption
const CHART_MARGIN_PX = 8; // tokens.json spacing.2

const STATUS_LABEL: Record<string, string> = {
  current: "On track",
  overdue: "Overdue",
  "paid-off": "Paid off",
};

function noteStatusTone(status: string): StatusTone {
  if (status === "overdue") return "danger";
  if (status === "paid-off") return "ok";
  return "progress";
}

function onTimeTone(pct: number): WidgetTone {
  if (pct >= 0.95) return "success";
  if (pct >= 0.7) return "warning";
  return "danger";
}

// ── Widget context ───────────────────────────────────────────────────────────

interface SellerHomeCtx {
  home: SellerHome;
  hero: SellerHomeHeroNote | null;
  goToTab: (tab: string) => void;
}

// ── Stat tiles ───────────────────────────────────────────────────────────────

function StatTilesWidget({ home, hero, multiNote }: {
  home: SellerHome;
  hero: SellerHomeHeroNote;
  multiNote: boolean;
}) {
  const behavior = home.paymentBehavior;
  // F-21: the next-payment and YTD tiles are HERO-NOTE-ONLY figures while the
  // principal tile aggregates — name the note when the seller holds several.
  const heroAttribution = multiNote ? ` · ${hero.loanNumber}` : "";
  return (
    // The tiles are already Cards — render them bare inside the grid (a full-
    // width row) rather than nesting them in another WidgetFrame card; the
    // boundary still keeps a render failure to this row only.
    <WidgetErrorBoundary span="full" testId="seller-home-tiles">
      <StatTileRow className="md:col-span-2 xl:col-span-3">
        {/* F-20: a state-retired note has no live next payment — hide the tile. */}
        {!hero.isRetired && (
          <StatTile
            label="Next payment on your note"
            value={hero.nextPaymentDate ? formatCurrency(hero.nextPaymentAmount) : "—"}
            hint={
              hero.nextPaymentDate
                ? `Buyer payment due ${formatCalendarDate(hero.nextPaymentDate)}${heroAttribution}`
                : "No payment scheduled"
            }
            testId="seller-home-tile-next"
          />
        )}
        <StatTile
          label="Collected on your note (YTD)"
          value={formatCurrency(hero.receivedYearToDate)}
          hint={`${multiNote ? `${hero.loanNumber} — ` : ""}${GROSS_FIGURES_CAPTION}`}
          testId="seller-home-tile-ytd"
        />
        <StatTile
          label="Principal owed to you"
          value={formatCurrency(home.totalRemainingPrincipal)}
          hint={multiNote ? `Across ${home.noteCount} notes` : undefined}
          testId="seller-home-tile-principal"
        />
        <StatTile
          label="Buyer on-time"
          value={
            behavior && behavior.onTimeCount + behavior.lateCount + behavior.missedCount > 0
              ? `${Math.round(behavior.onTimePercent * 100)}%`
              : "—"
          }
          hint={
            behavior && behavior.missedCount + behavior.lateCount > 0
              ? `${behavior.lateCount} late · ${behavior.missedCount} missed`
              : "On-time payments"
          }
          tone={behavior ? onTimeTone(behavior.onTimePercent) : "default"}
          testId="seller-home-tile-ontime"
        />
      </StatTileRow>
    </WidgetErrorBoundary>
  );
}

// ── Income trend ─────────────────────────────────────────────────────────────

function IncomeTrendWidget({ points }: { points: SellerIncomePoint[] }) {
  const data = points.map((p) => ({
    label: formatCalendarDate(p.periodDate, { month: "short", year: "2-digit" }),
    value: p.incomeReceived,
  }));
  return (
    // NET seller-routed disbursements, charted by the month the money moved
    // (F-22 server-side) — hence "Distributed to you", not "collected".
    <WidgetFrame title="Distributed to you over time" span="wide" testId="seller-home-trend">
      {/* currentColor keeps the series on the gold token — no hardcoded hex. */}
      <div className="h-40 w-full text-gold">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: CHART_MARGIN_PX, right: CHART_MARGIN_PX, bottom: 0, left: 0 }}
          >
            <XAxis dataKey="label" tick={{ fontSize: CHART_TICK_FONT_SIZE }} />
            <YAxis hide />
            <Area
              type="monotone"
              dataKey="value"
              stroke="currentColor"
              fill="currentColor"
              fillOpacity={0.15}
            />
            <Tooltip formatter={(v: number) => formatCurrency(v)} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </WidgetFrame>
  );
}

// ── Payoff countdown ─────────────────────────────────────────────────────────

function PayoffWidget({ hero }: { hero: SellerHomeHeroNote }) {
  if (hero.monthsToMaturity == null) return null;
  const total = hero.termMonths ?? null;
  const remaining = hero.monthsToMaturity;
  const elapsed = total != null ? Math.max(0, total - remaining) : null;
  const pct = total && total > 0 ? Math.min(1, Math.max(0, (elapsed ?? 0) / total)) : null;
  return (
    <WidgetFrame title="Payoff countdown" testId="seller-home-payoff">
      <div className="space-y-space-2">
        <p className="text-h3 font-semibold text-onyx m-0">
          {remaining} month{remaining === 1 ? "" : "s"} to payoff
        </p>
        {hero.maturityDate && (
          <p className="text-small text-gray-mid m-0">
            Matures {formatCalendarDate(hero.maturityDate)}
            {total != null && <> · {total}-month term</>}
          </p>
        )}
        {pct != null && <ProgressBar fraction={pct} testId="seller-home-payoff-progress" />}
      </div>
    </WidgetFrame>
  );
}

// ── Note roster ──────────────────────────────────────────────────────────────

function NoteRosterWidget({ home, onOpen }: { home: SellerHome; onOpen: () => void }) {
  return (
    <WidgetFrame
      title={`Your notes (${home.noteCount})`}
      span="full"
      testId="seller-home-notes"
      action={
        <Button variant="text" size="sm" onClick={onOpen} data-testid="seller-home-notes-open">
          View income & accounting
        </Button>
      }
    >
      <div className="space-y-space-2">
        {home.notes.map((n) => (
          <button
            key={n.loanId}
            type="button"
            onClick={onOpen}
            data-testid={`seller-home-note-${n.loanId}`}
            className="flex w-full flex-wrap items-center justify-between gap-space-3 rounded-lg border border-border-light bg-white-pure p-space-3 text-left transition-colors hover:border-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            <span className="flex items-center gap-space-2">
              <span className="text-small font-semibold text-onyx">{n.loanNumber}</span>
              <StatusBadge tone={noteStatusTone(n.status)}>
                {STATUS_LABEL[n.status] ?? n.status}
              </StatusBadge>
            </span>
            <span className="flex items-center gap-space-4">
              <span className="text-caption text-gray-mid">
                Owed to you{" "}
                <span className="text-small font-medium text-onyx">
                  {formatCurrency(n.remainingPrincipal)}
                </span>
              </span>
              <span className="text-caption text-gray-mid">
                Next{" "}
                <span className="text-small font-medium text-onyx">
                  {n.nextDueDate ? formatCalendarDate(n.nextDueDate) : "—"}
                </span>
              </span>
            </span>
          </button>
        ))}
      </div>
    </WidgetFrame>
  );
}

// ── Recipe ───────────────────────────────────────────────────────────────────

const NOTE_HOLDER_STAGES = ["note-serviced", "paid-off"] as const;

const SELLER_HOME_RECIPE: PortalHomeRecipe<SellerHomeCtx> = {
  widgets: [
    {
      key: "stat-tiles",
      stages: NOTE_HOLDER_STAGES,
      hidden: (c) => c.hero == null,
      render: (c) => (
        <StatTilesWidget home={c.home} hero={c.hero!} multiNote={c.home.noteCount > 1} />
      ),
    },
    {
      // F-20: never for paid-off stage, nor for a state-retired hero (a
      // retired_right_size note with residual balance must also hide it).
      key: "payoff",
      stages: ["note-serviced"],
      hidden: (c) =>
        c.hero == null ||
        c.hero.isRetired ||
        c.hero.status === "paid-off" ||
        c.hero.monthsToMaturity == null,
      render: (c) => <PayoffWidget hero={c.hero!} />,
    },
    {
      key: "income-trend",
      stages: NOTE_HOLDER_STAGES,
      hidden: (c) => c.hero == null || c.home.incomeTrend.length === 0,
      render: (c) => <IncomeTrendWidget points={c.home.incomeTrend} />,
    },
    {
      key: "note-roster",
      stages: NOTE_HOLDER_STAGES,
      hidden: (c) => c.hero == null || c.home.notes.length === 0,
      render: (c) => <NoteRosterWidget home={c.home} onOpen={() => c.goToTab("payments")} />,
    },
  ],
};

// ── Next best action (stage-derived) ─────────────────────────────────────────

function heroAction(
  home: SellerHome,
  stage: SellerStage,
  onViewPayments: () => void,
  onViewDeals: () => void,
): { heading: string; description?: string; tone: WidgetTone; actions?: ReactNode } {
  const hero = home.heroNote;

  if (stage === "pre-loan") {
    if (home.deal) {
      const multiDeal = (home.deal.dealCount ?? 1) > 1;
      const dealLine = `Deal ${home.deal.dealNumber}${
        home.deal.businessName ? ` — ${home.deal.businessName}` : ""
      }: ${home.deal.stageLabel}`;
      return {
        heading: multiDeal ? "Your sales are being finalized" : "Your sale is being finalized",
        description: multiDeal
          ? `You have ${home.deal.dealCount} sales in progress. Most recent: ${dealLine}. We'll let you know the moment your first note is boarded and payments begin.`
          : `${dealLine}. We'll let you know the moment your note is boarded and payments begin.`,
        tone: "default",
        actions: (
          <Button variant="secondary" size="sm" onClick={onViewDeals} data-testid="seller-home-view-deal">
            {multiDeal ? "View your deals" : "View deal status"}
          </Button>
        ),
      };
    }
    return {
      heading: "You're getting set up",
      description:
        "When your sale closes and your note is boarded, your monthly income and note balance will appear here.",
      tone: "default",
    };
  }

  if (stage === "paid-off") {
    return {
      heading: "Your note is paid in full",
      description: "Nothing left to collect. Thank you — congratulations!",
      tone: "success",
    };
  }

  // note-serviced
  if (hero && hero.status === "overdue") {
    return {
      heading: `A payment on ${hero.loanNumber} is behind`,
      description:
        hero.daysDelinquent > 0
          ? `The buyer is ${hero.daysDelinquent} day${
              hero.daysDelinquent === 1 ? "" : "s"
            } past due. Phoenix is handling collection — you don't need to contact the borrower.`
          : "Phoenix is handling collection — you don't need to contact the borrower.",
      tone: "danger",
      actions: (
        <Button variant="secondary" size="sm" onClick={onViewPayments} data-testid="seller-home-view-payments">
          View income & accounting
        </Button>
      ),
    };
  }

  if (hero && hero.nextPaymentDate) {
    return {
      // F-03 (locked): this is the BUYER's gross payment on the note, not the
      // seller's net income — say so, with the explicit gross caption.
      heading: `Next payment on your note: ${formatCurrency(hero.nextPaymentAmount)} due ${formatCalendarDate(
        hero.nextPaymentDate,
      )}`,
      description: `The buyer's payment on ${hero.loanNumber} is due then. ${GROSS_FIGURES_CAPTION}`,
      tone: "default",
      actions: (
        <Button variant="text" size="sm" onClick={onViewPayments} data-testid="seller-home-view-payments">
          View income & accounting
        </Button>
      ),
    };
  }

  return {
    heading: "Your note is being serviced",
    description: "Payments flow to you monthly. Your income and schedule live under Income & accounting.",
    tone: "default",
    actions: (
      <Button variant="text" size="sm" onClick={onViewPayments} data-testid="seller-home-view-payments">
        View income & accounting
      </Button>
    ),
  };
}

// ── Tab ──────────────────────────────────────────────────────────────────────

export function SellerHomeTab() {
  const home = useWidgetData(() => portalSellerFinanceApi.home(), []);
  const [, setParams] = useSearchParams();

  const goToTab = useCallback(
    (tab: string) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set("tab", tab);
          return updated;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  if (home.status === "error" && home.data == null) {
    return <PortalHomeLayout error={home.error} onRetry={home.reload} testId="seller-home-error" />;
  }
  if (home.data == null) {
    return <PortalHomeLayout loading testId="seller-home-loading" />;
  }
  const data = home.data;

  // F-39: narrow the server stage with the shared guard (never an unchecked
  // cast) — an unknown code from a newer backend degrades to the closest
  // sensible branch instead of mis-rendering.
  const stage: SellerStage = asSellerStage(data.stage) ?? (data.heroNote ? "note-serviced" : "pre-loan");
  const pos = sellerSpine(stage);
  const action = heroAction(
    data,
    stage,
    () => goToTab("payments"),
    () => goToTab("deals"),
  );

  return (
    <PortalHomeLayout stage={stage} testId="seller-home">
      <JourneySpine
        steps={SELLER_SPINE}
        currentIndex={pos.index}
        complete={pos.complete}
        testId="seller-home-spine"
      />

      <NextBestActionCard
        heading={action.heading}
        description={action.description}
        actions={action.actions}
        tone={action.tone}
        testId={`seller-home-hero-${stage}`}
      />

      {/* Outstanding document requests — a SIBLING of the hero above, in the
          same position on every stage, and ABOVE the no-note empty state: a
          pre-loan seller with paperwork outstanding should read "3 items need
          you" before "No note yet", because only one of those is actionable.
          heroAction knows only notes and stage, so it can never say this. */}
      <PortalRequestsStrip testId="seller-home-requests" />

      {stage === "pre-loan" && !data.deal && (
        <EmptyState
          title="No note yet"
          description="Once your sale is funded and your note is boarded, your income, remaining balance, and payment history will appear here."
        />
      )}

      <PortalHomeDashboard
        recipe={SELLER_HOME_RECIPE}
        ctx={{ home: data, hero: data.heroNote, goToTab }}
        stage={stage}
        testId="seller-home-widgets"
      />
    </PortalHomeLayout>
  );
}
